import React, { useState } from "react";
import { Add, ExpandMore } from "@mui/icons-material";
import { Chip, Avatar, Menu, MenuItem, Link, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import Skeleton from "react-loading-skeleton";
import { useHasPermission } from "../utils/permissionUtils";
import { getAvatarUrl } from "../utils/avatarUtils";
import { useSelector } from "react-redux";
import SendNotificationDialog from "./SendNotificationDialog";
import ScheduleMeetingDialog from "./ScheduleMeetingDialog";

const ManagerDropDownItem = styled(MenuItem)(({ theme }) => ({
    textDecoration: 'none',
    color: theme.palette.text.primary,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}))

const AvatarSlot = styled('span')({
    position: 'relative',
    display: 'inline-flex',
    flexShrink: 0,
    '& .MuiAvatar-root': {
        width: 'inherit',
        height: 'inherit',
        fontSize: 'inherit',
    },
})

const StatusDot = styled('span', { shouldForwardProp: (p) => p !== 'dotcolor' })(({ theme, dotcolor }) => ({
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '36%',
    height: '36%',
    minWidth: 6,
    minHeight: 6,
    borderRadius: '50%',
    backgroundColor: theme.palette[dotcolor] && theme.palette[dotcolor].main ? theme.palette[dotcolor].main : theme.palette.action.disabled,
    border: `1.5px solid ${theme.palette.background.paper}`,
    boxSizing: 'content-box',
}))

const UserMenuChip = ({ user, menuItems = {}, size = "large", onReAssign, canSeeUnassigned = true, disabled = false }) => {
    const [responsibleMenuAnchorEl, setResponsibleMenuAnchorEl] = useState(null);
    const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
    const [scheduleMeetingOpen, setScheduleMeetingOpen] = useState(false);
    const navigate = useNavigate();
    const hasPermission = useHasPermission();
    const canReassign = hasPermission('assignTask');
    const canSendNotifications = hasPermission('canSendNotifications');
    const canScheduleMeetings = hasPermission('scheduleMeetings');
    const canManageCalendar = hasPermission('manageCalendar');
    const userId = useSelector(state => state.auth.userId);
    const canShowScheduleMeeting = (canScheduleMeetings || canManageCalendar)
        && user?.calendarOptOut !== true
        && userId !== user?._id;

    const handleResponsibleMenuClick = (event) => {
        if (!disabled) {
            setResponsibleMenuAnchorEl(event.currentTarget);
        }
    };

    const handleResponsibleMenuChildClick = (event) => {
        if (!disabled) {
            const parentElement = event.currentTarget.parentElement;
            setResponsibleMenuAnchorEl(parentElement);
        }
    }

    const handleResponsibleMenuClose = () => {
        setResponsibleMenuAnchorEl(null);
    };

    const defaultMenuItems = {
        viewProfile: true,
        sendEmail: true,
        scheduleMeeting: true,
        reAssign: false,
        ...menuItems
    };

    const handleReAssign = () => {
        handleResponsibleMenuClose();
        if (onReAssign) {
            onReAssign();
        }
    };

    const handleSendNotification = () => {
        handleResponsibleMenuClose();
        setNotificationDialogOpen(true);
    };

    const handleScheduleMeeting = () => {
        handleResponsibleMenuClose();
        setScheduleMeetingOpen(true);
    };

    const hasAvailabilityData = user && (user.isAvailable !== undefined || user.isWorking !== undefined);
    const dotColor = user?.isWorking ? "success" : user?.isAvailable ? "warning" : "default";
    const availabilityLabel = user?.isWorking ? "Working" : user?.isAvailable ? "Available" : "Unavailable";

    const renderAvatar = () => (
        <AvatarSlot>
            <Avatar src={getAvatarUrl(user)} />
            {hasAvailabilityData && <StatusDot dotcolor={dotColor} />}
        </AvatarSlot>
    );

    const tooltipTitle = (base) => (hasAvailabilityData ? `${base} — ${availabilityLabel}` : base);

    const renderLargeChip = () => (
        <Tooltip title={tooltipTitle(user?.fullName || "Loading...")} placement="top-end">
            <Chip
                size="small"
                color={(disabled || !user?._id) ? "default" : "success"}
                variant="outlined"
                label={user?.fullName || <Skeleton />}
                deleteIcon={<ExpandMore />}
                avatar={renderAvatar()}
                onClick={handleResponsibleMenuClick}
                onDelete={handleResponsibleMenuChildClick}
                disabled={disabled}
            />
        </Tooltip>
    );

    const renderSmallChip = () => (
        <Tooltip title={tooltipTitle(user?.fullName || "Loading...")} placement="top-end">
            <Chip
                size="small"
                color={(disabled || !user?._id) ? "default" : "success"}
                variant="outlined"
                label={user?.first_name || <Skeleton />}
                deleteIcon={<ExpandMore />}
                avatar={renderAvatar()}
                onClick={handleResponsibleMenuClick}
                onDelete={handleResponsibleMenuChildClick}
                disabled={disabled}
            />
        </Tooltip>
    );

    return (
        <>
            {user ? (
                <>
                    {size === "small" ? renderSmallChip() : renderLargeChip()}
                    <Menu
                        anchorEl={responsibleMenuAnchorEl}
                        open={Boolean(responsibleMenuAnchorEl) && !disabled}
                        onClose={handleResponsibleMenuClose}
                    >
                        {defaultMenuItems.viewProfile && (
                            <ManagerDropDownItem component={Link} onClick={() => navigate(`/user/${user?._id}`)} >View Profile</ManagerDropDownItem>
                        )}
                        {defaultMenuItems.sendEmail && userId !== user._id && (
                            <ManagerDropDownItem component={Link} onClick={handleResponsibleMenuClose} href={`mailto:${user?.email}`}>Send Email</ManagerDropDownItem>
                        )}
                        {defaultMenuItems.scheduleMeeting && canShowScheduleMeeting && (
                            <ManagerDropDownItem onClick={handleScheduleMeeting}>Schedule Meeting</ManagerDropDownItem>
                        )}
                        {defaultMenuItems.reAssign && canReassign && (
                            <ManagerDropDownItem onClick={handleReAssign}>Re-assign</ManagerDropDownItem>
                        )}
                        {canSendNotifications && userId !== user._id && (
                            <ManagerDropDownItem onClick={handleSendNotification}>Send Notification</ManagerDropDownItem>
                        )}
                    </Menu>
                    {notificationDialogOpen && (
                        <SendNotificationDialog
                            open={notificationDialogOpen}
                            onClose={() => setNotificationDialogOpen(false)}
                            recipient={user}
                        />
                    )}
                    {scheduleMeetingOpen && (
                        <ScheduleMeetingDialog
                            open={scheduleMeetingOpen}
                            onClose={() => setScheduleMeetingOpen(false)}
                            defaultAttendees={[user]}
                        />
                    )}
                </>
            ) : (
                <>
                    {canSeeUnassigned && (
                        <Chip
                            size={size}
                            color="default"
                            variant="outlined"
                            label="Assign"
                            avatar={<Add />}
                            onClick={handleReAssign}
                            disabled={disabled}
                        />
                    )}
                </>
            )}
        </>
    );
}

export default UserMenuChip;